import clsx from 'clsx';
import type { ReactNode } from 'react';
import { Reorder, motion, useDragControls } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ElementsButton, HeadlessPercentageInput, Icon, InputContainer, elementsInputClasses } from '../../UI';
import type { EstateStatusChecklistItem } from '../types';

export type EstateStatusTemplateEditorItem = Pick<EstateStatusChecklistItem, 'id' | 'label' | 'weight'>;

export interface EstateStatusTemplateEditorProps {
  items: EstateStatusTemplateEditorItem[];
  onChange: (items: EstateStatusTemplateEditorItem[]) => void;
  className?: string;
  isDisabled?: boolean;
  emptyState?: ReactNode;
}

function updateItem(
  items: EstateStatusTemplateEditorItem[],
  itemIndex: number,
  patch: Partial<EstateStatusTemplateEditorItem>
) {
  return items.map((item, currentIndex) => (currentIndex === itemIndex ? { ...item, ...patch } : item));
}

function normalizeWeight(value: number | null | undefined) {
  if (value === null || value === undefined) return 0;

  return Math.max(0, Math.round(value));
}

function createTemplateItemID() {
  return `estate-status-template-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function addTemplateItem(items: EstateStatusTemplateEditorItem[], label: string) {
  return [
    ...items,
    {
      id: createTemplateItemID(),
      label,
      weight: 0,
    },
  ];
}

function deleteItem(items: EstateStatusTemplateEditorItem[], itemIndex: number) {
  return items.filter((_item, currentIndex) => currentIndex !== itemIndex);
}

export function reorderTemplateItems(items: EstateStatusTemplateEditorItem[], nextItemIDs: string[]) {
  const itemsByID = new Map(items.map((item) => [item.id, item]));

  return nextItemIDs.flatMap((itemID) => {
    const item = itemsByID.get(itemID);

    return item ? [item] : [];
  });
}

interface EstateStatusTemplateEditorRowProps {
  item: EstateStatusTemplateEditorItem;
  itemIndex: number;
  items: EstateStatusTemplateEditorItem[];
  onChange: (items: EstateStatusTemplateEditorItem[]) => void;
  isDisabled?: boolean;
}

function EstateStatusTemplateEditorRow({
  item,
  itemIndex,
  items,
  onChange,
  isDisabled,
}: EstateStatusTemplateEditorRowProps) {
  const { t } = useTranslation('estateStatus');
  const dragControls = useDragControls();
  const rowDisabled = !!isDisabled;

  return (
    <Reorder.Item
      as="div"
      value={item.id}
      dragListener={false}
      dragControls={dragControls}
      data-testid={`estate-status-template-item-${item.id}`}
      whileDrag={{
        scale: 1.01,
        boxShadow: '0 6px 12px 4px rgba(0, 0, 0, 0.2), inset 0 1px 0 0 rgba(88, 95, 102, 0.32)',
      }}
      className="rounded-3xl border border-darkMode-gray-medium/20 bg-darkMode-black/50 p-4"
    >
      <div className="grid gap-4 md:grid-cols-[auto,minmax(0,1fr),180px,auto] md:items-center">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-darkMode-gray-medium/20 text-sm font-semibold text-white">
            {itemIndex + 1}
          </div>

          <motion.button
            type="button"
            aria-label={t('template-reorder-item', { label: item.label })}
            whileHover={{ scale: rowDisabled ? 1 : 1.08 }}
            whileTap={{ scale: rowDisabled ? 1 : 0.96 }}
            disabled={rowDisabled}
            className={clsx(
              'touch-none select-none text-darkMode-gray-light',
              rowDisabled && 'cursor-not-allowed opacity-50'
            )}
            onPointerDown={(event) => {
              if (rowDisabled) return;
              dragControls.start(event);
            }}
          >
            <Icon icon="dragHandle" className="h-6 w-6" />
          </motion.button>
        </div>

        <InputContainer
          className="w-full"
          label={t('template-label-input')}
          size="small"
          status={rowDisabled ? 'disabled' : 'normal'}
        >
          <input
            aria-label={t('template-label-input')}
            value={item.label}
            disabled={rowDisabled}
            className={clsx(elementsInputClasses.input, 'min-w-0')}
            onChange={(event) => onChange(updateItem(items, itemIndex, { label: event.currentTarget.value }))}
          />
        </InputContainer>

        <InputContainer
          className="w-full md:max-w-[180px]"
          label={t('template-weight-input')}
          size="small"
          status={rowDisabled ? 'disabled' : 'normal'}
        >
          <HeadlessPercentageInput
            name={`estate-status-template-item-weight-${item.id}`}
            value={item.weight}
            decimalsLimit={0}
            disabled={rowDisabled}
            className={clsx(elementsInputClasses.input, 'min-w-0 text-right')}
            onChange={(value) => onChange(updateItem(items, itemIndex, { weight: normalizeWeight(value) }))}
          />
        </InputContainer>

        <ElementsButton
          type="button"
          size="small"
          theme="text"
          icon="delete"
          label={t('template-delete-item')}
          className="justify-self-start md:justify-self-end"
          disabled={rowDisabled}
          onClick={() => onChange(deleteItem(items, itemIndex))}
        />
      </div>
    </Reorder.Item>
  );
}

export function EstateStatusTemplateEditor({
  items,
  onChange,
  className,
  isDisabled,
  emptyState,
}: EstateStatusTemplateEditorProps) {
  const { t } = useTranslation('estateStatus');
  const renderedEmptyState = emptyState ?? t('template-empty-state');

  return (
    <div className={clsx('space-y-4', className)}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm leading-6 text-darkMode-gray-light/70">{t('template-editor-instructions')}</p>
        <ElementsButton
          type="button"
          size="small"
          theme="secondary"
          icon="add"
          label={t('template-add-item')}
          disabled={isDisabled}
          onClick={() => onChange(addTemplateItem(items, t('template-new-item-label')))}
        />
      </div>

      {items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-darkMode-gray-medium/30 bg-darkMode-black/40 p-6 text-sm text-darkMode-gray-light/75">
          {renderedEmptyState}
        </div>
      ) : (
        <Reorder.Group
          axis="y"
          values={items.map((item) => item.id)}
          onReorder={(nextItemIDs) => onChange(reorderTemplateItems(items, nextItemIDs))}
          className="space-y-3"
        >
          {items.map((item, itemIndex) => {
            return (
              <EstateStatusTemplateEditorRow
                key={item.id}
                item={item}
                itemIndex={itemIndex}
                items={items}
                onChange={onChange}
                isDisabled={isDisabled}
              />
            );
          })}
        </Reorder.Group>
      )}
    </div>
  );
}
